/**
 * Firestore Safe Helpers
 * FinTasks 2.0 - Phase 1
 * 
 * Enterprise-grade Firestore write helpers that sanitize data
 * to prevent null/undefined values from causing write errors.
 * 
 * SAFETY: These are NEW helper functions - they do NOT modify
 * any existing service functions.
 */

import {
  doc,
  setDoc,
  addDoc,
  updateDoc,
  collection,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@/config/firebase';

// ============================================
// SANITIZATION HELPERS
// ============================================

/**
 * Recursively removes undefined values from an object.
 * Firestore does not accept undefined, so we strip them.
 * 
 * @param {Object} obj - The object to sanitize
 * @returns {Object} - Object with undefined values removed
 */
export function removeUndefined(obj) {
  if (obj === null || obj === undefined) {
    return null;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeUndefined).filter(item => item !== undefined);
  }

  if (typeof obj === 'object' && obj !== null) {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    }
    return cleaned;
  }

  return obj;
}

/**
 * Converts null values to a specified default value.
 * Useful when you want to preserve nulls as empty strings or default values.
 * 
 * @param {Object} obj - The object to process
 * @param {Object} defaults - Map of field names to default values for nulls
 * @returns {Object} - Object with nulls replaced by defaults
 */
export function applyDefaults(obj, defaults = {}) {
  if (!obj || typeof obj !== 'object') return obj;

  const result = { ...obj };
  
  for (const [key, defaultValue] of Object.entries(defaults)) {
    if (result[key] === null || result[key] === undefined) {
      result[key] = defaultValue;
    }
  }

  return result;
}

/**
 * Validates required fields are present and non-empty.
 * 
 * @param {Object} data - The data object to validate
 * @param {string[]} requiredFields - Array of required field names
 * @throws {Error} - If any required field is missing or empty
 */
export function validateRequired(data, requiredFields = []) {
  const missing = [];
  
  for (const field of requiredFields) {
    const value = data[field];
    if (value === undefined || value === null || value === '') {
      missing.push(field);
    }
  }

  if (missing.length > 0) {
    throw new Error(`Missing required fields: ${missing.join(', ')}`);
  }

  return true;
}

/**
 * Sanitizes a data object for Firestore write operations.
 * - Removes undefined values
 * - Trims string values
 * - Ensures arrays are valid
 * 
 * @param {Object} data - The raw data to sanitize
 * @returns {Object} - Sanitized data safe for Firestore
 */
export function sanitizeForFirestore(data) {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sanitized = {};

  for (const [key, value] of Object.entries(data)) {
    // Skip undefined values
    if (value === undefined) continue;

    // Trim strings
    if (typeof value === 'string') {
      sanitized[key] = value.trim();
      continue;
    }

    // Recursively sanitize nested objects (but not Timestamps/Dates)
    if (value !== null && typeof value === 'object') {
      // Check if it's a Firestore Timestamp or Date - don't modify
      if (value.toDate || value instanceof Date) {
        sanitized[key] = value;
        continue;
      }

      // Handle arrays
      if (Array.isArray(value)) {
        sanitized[key] = value
          .filter(item => item !== undefined)
          .map(item => typeof item === 'object' ? sanitizeForFirestore(item) : item);
        continue;
      }

      // Nested object
      sanitized[key] = sanitizeForFirestore(value);
      continue;
    }

    // All other values (numbers, booleans, null)
    sanitized[key] = value;
  }

  return sanitized;
}

// ============================================
// SAFE FIRESTORE OPERATIONS
// ============================================

/**
 * Safely adds a document to a collection with sanitized data.
 * 
 * @param {string} path - Collection path (e.g., 'users/uid/todoLists')
 * @param {Object} data - Document data to add
 * @param {Object} options - Optional configuration
 * @param {boolean} options.addTimestamps - Add createdAt/updatedAt (default: true)
 * @param {string[]} options.requiredFields - Fields that must be present
 * @returns {Promise<{id: string, ...data}>} - Created document with ID
 */
export async function safeAddDoc(path, data, options = {}) {
  const { 
    addTimestamps = true, 
    requiredFields = [] 
  } = options;

  // Validate required fields
  if (requiredFields.length > 0) {
    validateRequired(data, requiredFields);
  }

  // Sanitize data
  let sanitizedData = sanitizeForFirestore(data);

  // Add timestamps if requested
  if (addTimestamps) {
    sanitizedData = {
      ...sanitizedData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
  }

  try {
    const collectionRef = collection(db, ...path.split('/'));
    const docRef = await addDoc(collectionRef, sanitizedData);

    console.log(`[safeAddDoc] Document created at ${path}/${docRef.id}`);

    return {
      id: docRef.id,
      ...sanitizedData,
      // Convert serverTimestamp to ISO for immediate use
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error(`[safeAddDoc] Error adding document to ${path}:`, error);
    throw error;
  }
}

/**
 * Safely sets a document with sanitized data (creates or overwrites).
 * 
 * @param {string} path - Full document path (e.g., 'users/uid/todoLists/listId')
 * @param {Object} data - Document data to set
 * @param {Object} options - Optional configuration
 * @param {boolean} options.merge - Merge with existing data (default: false)
 * @param {boolean} options.addTimestamps - Add createdAt/updatedAt (default: true)
 * @returns {Promise<{id: string, ...data}>} - Set document data
 */
export async function safeSetDoc(path, data, options = {}) {
  const { 
    merge = false, 
    addTimestamps = true 
  } = options;

  // Sanitize data
  let sanitizedData = sanitizeForFirestore(data);

  // Add timestamps
  if (addTimestamps) {
    sanitizedData = {
      ...sanitizedData,
      updatedAt: serverTimestamp()
    };
    
    if (!merge) {
      sanitizedData.createdAt = serverTimestamp();
    }
  }

  try {
    const pathParts = path.split('/');
    const docRef = doc(db, ...pathParts);
    
    await setDoc(docRef, sanitizedData, { merge });

    const docId = pathParts[pathParts.length - 1];
    console.log(`[safeSetDoc] Document set at ${path}`);

    return {
      id: docId,
      ...sanitizedData,
      updatedAt: new Date().toISOString(),
      ...(merge ? {} : { createdAt: new Date().toISOString() })
    };
  } catch (error) {
    console.error(`[safeSetDoc] Error setting document at ${path}:`, error);
    throw error;
  }
}

/**
 * Safely updates a document with sanitized data (partial update).
 * 
 * @param {string} path - Full document path (e.g., 'users/uid/todoLists/listId')
 * @param {Object} updates - Fields to update
 * @param {Object} options - Optional configuration
 * @param {boolean} options.addTimestamp - Add updatedAt timestamp (default: true)
 * @returns {Promise<boolean>} - True if successful
 */
export async function safeUpdateDoc(path, updates, options = {}) {
  const { addTimestamp = true } = options;

  // Sanitize data
  let sanitizedUpdates = sanitizeForFirestore(updates);

  // Add updated timestamp
  if (addTimestamp) {
    sanitizedUpdates = {
      ...sanitizedUpdates,
      updatedAt: serverTimestamp()
    };
  }

  try {
    const pathParts = path.split('/');
    const docRef = doc(db, ...pathParts);
    
    await updateDoc(docRef, sanitizedUpdates);

    console.log(`[safeUpdateDoc] Document updated at ${path}`);
    return true;
  } catch (error) {
    console.error(`[safeUpdateDoc] Error updating document at ${path}:`, error);
    throw error;
  }
}

/**
 * Builds a Firestore path string from parts.
 * 
 * @param {...string} parts - Path segments
 * @returns {string} - Joined path
 * 
 * @example
 * buildPath('users', userId, 'todoLists', listId, 'tasks')
 * // => 'users/abc123/todoLists/xyz789/tasks'
 */
export function buildPath(...parts) {
  return parts.filter(Boolean).join('/');
}

// ============================================
// TASK-SPECIFIC SANITIZATION
// ============================================

/**
 * Default values for task fields.
 * Used to ensure consistent task structure.
 * 
 * Phase 2 additions: Smart task linking fields (all optional, backward compatible)
 */
export const TASK_DEFAULTS = {
  // Core fields
  title: '',
  description: '',
  status: 'todo',
  priority: 'medium',
  isFinancialTask: false,
  estimatedAmount: 0,
  currency: 'USD',
  tags: [],
  
  // Existing linking fields
  linkedType: 'none',
  linkedRef: '',
  linkedLabel: '',
  contextType: 'none',
  contextId: '',
  contextLabel: '',
  linkedEnvelopeId: null,
  goalId: null,
  
  // Date fields
  dueDate: null,
  reminderAt: null,
  recurrenceType: 'none',
  
  // Phase 2: Smart task linking (all optional)
  relatedBudgetId: null,
  relatedCategoryId: null,
  relatedBillId: null,
  relatedTransactionId: null,
  relatedGoalId: null,
  
  // Phase 2: Smart task metadata
  smartTaskType: null,
  smartTaskTrigger: null,
  autoCompleteOn: null,
  expiresAt: null,
  deduplicationKey: null
};

/**
 * Sanitizes task data specifically, applying task defaults.
 * 
 * @param {Object} taskData - Raw task data
 * @returns {Object} - Sanitized task data with defaults applied
 */
export function sanitizeTaskData(taskData) {
  // First apply general sanitization
  const sanitized = sanitizeForFirestore(taskData);

  // Apply task-specific defaults for missing fields
  return {
    ...TASK_DEFAULTS,
    ...sanitized,
    // Ensure arrays are arrays
    tags: Array.isArray(sanitized.tags) ? sanitized.tags : [],
    // Ensure financial amount is a number
    estimatedAmount: parseFloat(sanitized.estimatedAmount) || 0,
    // Ensure boolean flags
    isFinancialTask: Boolean(sanitized.isFinancialTask)
  };
}

export default {
  removeUndefined,
  applyDefaults,
  validateRequired,
  sanitizeForFirestore,
  safeAddDoc,
  safeSetDoc,
  safeUpdateDoc,
  buildPath,
  sanitizeTaskData,
  TASK_DEFAULTS
};
